import React, { useContext } from 'react';
import { ThemeProvider } from 'emotion-theming';
import { LargeScreenOnly, MEDIA_QUERIES as M } from '../../../foundations/grid';
import { ColorCodes, colorsForTheme } from '../../../foundations/color';
import { genThemedStyled, Theme, ThemeContext } from '../../../utilities/theme';
import S from '../../../foundations/size';
import { FUTURO_TYPEFACES as F } from '../../../foundations/font';
import Divider from '../../../components/Divider';

type Props = {
  heading: string;
  subheading?: string;
  children: React.ReactNode;
  footerActions?: React.ReactNode;
  startAlign?: boolean; // align children to flex-start
};

type themeStyles = {
  backgroundColor: ColorCodes;
  headingColor: ColorCodes;
  subHeadingColor: ColorCodes;
  textColor: ColorCodes;
  footerTextColor: ColorCodes;
};

const getTheme = (theme: Theme): themeStyles => {
  const colors = colorsForTheme(theme);
  const colorThemes = {
    backgroundColor: colors.SWAN,
    headingColor: colors.STORM,
    subHeadingColor: colors.STEEL,
    textColor: colors.STEEL,
    footerTextColor: colors.SPACE,
  };
  return colorThemes;
};

const ThemeStyled = genThemedStyled<themeStyles>();

const Wrapper = ThemeStyled.div(p => ({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  gap: S.S,
  backgroundColor: p.theme.backgroundColor,
  padding: S.XXXXL,
  paddingLeft: '7.188rem',

  [M.SMALL_DESKTOP_AND_SMALLER]: {
    padding: `${S.XL} 4rem ${S.XXXXL}`,
  },
  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    padding: S.XXXL,
    margin: 0,
  },
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    padding: S.XL,
    paddingTop: 0,
    margin: 0,
    backgroundColor: 'unset',
  },
  [M.MOBILE_ONLY]: {
    padding: S.S,
    paddingBottom: S.XL,
    margin: 0,
    backgroundColor: 'unset',
  },
}));

const Header = ThemeStyled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: S.S,

  [M.SMALL_DESKTOP_AND_SMALLER]: {
    marginTop: S.S,
  },
  [M.TABLET_LANDSCAPE_AND_SMALLER]: {
    marginTop: 'unset',
  },
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    flexDirection: 'column-reverse',
  },
});

const Heading = ThemeStyled.h4(
  p => ({
    color: p.theme.headingColor,
    width: '21rem',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    [M.TABLET_PORTRAIT_AND_SMALLER]: {
      width: '75%',
    },
    [M.MOBILE_ONLY]: {
      width: '100%',
    },
  }),
  F.H4
);

const Subheading = ThemeStyled.h5(
  p => ({
    color: p.theme.subHeadingColor,
    margin: 0,
    width: S.XXXXL,
  }),
  F.H5
);

const Content = ThemeStyled.div<{ startAlign: boolean }>(({ startAlign }) => ({
  display: 'flex',
  justifyContent: startAlign ? 'flex-start' : 'center',
  width: '100%',
  maxWidth: '30.5rem',
  margin: 'auto',
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    maxWidth: '100%',
  },
}));

const DividerContainer = ThemeStyled.div({
  marginBottom: S.L,
});

const ActionsContainer = ThemeStyled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  [M.TABLET_PORTRAIT_AND_SMALLER]: {
    flexDirection: 'column-reverse',
  },
});

const RightSideContent = (props: Props) => {
  const {
    heading,
    subheading,
    children,
    footerActions,
    startAlign = false,
  } = props;

  const theme = getTheme(useContext(ThemeContext));

  return (
    <ThemeProvider theme={theme}>
      <Wrapper>
        <Header>
          <Heading>{heading}</Heading>

          {subheading && <Subheading>{subheading}</Subheading>}
        </Header>

        <Content startAlign={startAlign}>{children}</Content>

        <div className="block">
          <LargeScreenOnly mediaQuery={M.TABLET_LANDSCAPE_AND_SMALLER}>
            <DividerContainer>
              <Divider type="regular" />
            </DividerContainer>
          </LargeScreenOnly>

          {footerActions && (
            <ActionsContainer>{footerActions}</ActionsContainer>
          )}
        </div>
      </Wrapper>
    </ThemeProvider>
  );
};

export default RightSideContent;
